import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../../API';
import { isCaseScope, isProtocolScope } from '../utils/blockScope';

function apiErrorMessage(err, fallback) {
    return err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || fallback;
}

/**
 * Block create / update / delete / region attach-detach + case refresh.
 * @param {import('../utils/blockScope').BlockScope} scope
 */
export function useBlockMutations(scope) {
    const queryClient = useQueryClient();

    const refetchAfterCaseChange = useCallback(async () => {
        if (!isCaseScope(scope) || !scope.caseId || !scope.setDoc) return null;
        queryClient.invalidateQueries({ queryKey: ['bdsa-case', scope.caseId] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-case-list'] });
        const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: scope.caseId } });
        if (caseRes?.status === 200 && caseRes?.data) {
            scope.setDoc(caseRes.data);
            return caseRes.data;
        }
        return null;
    }, [scope, queryClient]);

    const invalidateProtocolBlocks = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['bdsa-block'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-region'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-metadata-options'] });
    }, [queryClient]);

    const createBlock = useCallback(async ({ name, regionIds = [], schemaRegionKeys = [], regionDetails, filenamePattern }) => {
        const body = isCaseScope(scope)
            ? { name, bdsa_case_id: scope.doc._id }
            : { name, protocol_id: scope.protocolId };
        if (filenamePattern) body.filename_pattern = filenamePattern;

        const response = await api.post('/bdsa/block', { body });
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to create block'));
        }
        const newBlockId = response.data?._id;
        if (newBlockId && (regionIds.length > 0 || schemaRegionKeys.length > 0)) {
            const regionBody = {};
            if (regionIds.length > 0) regionBody.regionIds = regionIds;
            if (schemaRegionKeys.length > 0) regionBody.schemaRegionKeys = schemaRegionKeys;
            if (Array.isArray(regionDetails) && regionDetails.length === regionIds.length + schemaRegionKeys.length) {
                regionBody.regionDetails = regionDetails;
            }
            const regionsResponse = await api.post(`/bdsa/block/${newBlockId}/regions`, { body: regionBody });
            if (regionsResponse.status !== 200) {
                throw new Error(apiErrorMessage({ response: regionsResponse }, 'Failed to add regions to block'));
            }
        }
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response.data;
    }, [scope, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const updateBlockName = useCallback(async ({ blockId, name, protocolId, filenamePattern }) => {
        const body = { name };
        if (protocolId !== undefined) body.protocol_id = protocolId;
        if (filenamePattern !== undefined) body.filename_pattern = filenamePattern;
        const response = await api.put(`/bdsa/block/${blockId}`, { body });
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to update block'));
        }
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response.data;
    }, [scope, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const updateBlockPattern = useCallback(async ({ blockId, filenamePattern, protocolId }) => {
        const body = { filename_pattern: filenamePattern ?? '' };
        if (protocolId !== undefined) body.protocol_id = protocolId;
        const response = await api.put(`/bdsa/block/${blockId}`, { body });
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to update block pattern'));
        }
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response.data;
    }, [scope, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const deleteBlock = useCallback(async (blockId) => {
        const response = await api.delete(`/bdsa/block/${blockId}`);
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to delete block'));
        }
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response;
    }, [scope, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const addRegionsToBlock = useCallback(async (blockId, { regionIds, schemaRegionKeys, regionDetails }) => {
        const body = {};
        if (regionIds?.length) body.regionIds = regionIds;
        if (schemaRegionKeys?.length) body.schemaRegionKeys = schemaRegionKeys;
        if (regionDetails) body.regionDetails = regionDetails;
        const response = await api.post(`/bdsa/block/${blockId}/regions`, { body });
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to add regions'));
        }
        queryClient.invalidateQueries({ queryKey: ['bdsa-region', 'block', blockId] });
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response.data;
    }, [scope, queryClient, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const removeRegionFromBlock = useCallback(async (regionId, blockId) => {
        const response = await api.delete(`/bdsa/region/${regionId}`);
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to remove region'));
        }
        if (blockId) {
            queryClient.invalidateQueries({ queryKey: ['bdsa-region', 'block', blockId] });
        }
        if (isCaseScope(scope)) await refetchAfterCaseChange();
        else invalidateProtocolBlocks();
        return response;
    }, [scope, queryClient, refetchAfterCaseChange, invalidateProtocolBlocks]);

    const refreshCaseFromProtocol = useCallback(async () => {
        if (!isCaseScope(scope) || !scope.doc?._id) {
            throw new Error('refreshCaseFromProtocol requires case scope');
        }
        const response = await api.post(`/bdsa/case/${scope.doc._id}/refresh-protocol-blocks`);
        if (response.status !== 200) {
            throw new Error(apiErrorMessage({ response }, 'Failed to refresh blocks from protocol'));
        }
        await refetchAfterCaseChange();
        return response.data;
    }, [scope, refetchAfterCaseChange]);

    return {
        createBlock,
        updateBlockName,
        updateBlockPattern,
        deleteBlock,
        addRegionsToBlock,
        removeRegionFromBlock,
        refreshCaseFromProtocol,
        refetchAfterCaseChange,
        isCaseScope: isCaseScope(scope),
        isProtocolScope: isProtocolScope(scope),
    };
}
